import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0'};
  border-radius: 12px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'};
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#0F172A'};
`;

const Description = styled.p`
  font-size: 13px;
  margin: 0;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.muted || '#64748B'
      : '#64748B'};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AllLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) => props.theme.colors?.primary || '#FA4616'};
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0'};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${(props) =>
      props.disabled
        ? 'transparent'
        : props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)'};
  }
`;

const OptionLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#0F172A'};
`;

// Radio button styled component
const RadioWrapper = styled.div`
  position: relative;
  width: 44px;
  height: 24px;
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  z-index: 1;
  margin: 0;
`;

const RadioTrack = styled.div`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: ${(props) =>
    props.checked
      ? props.theme.colors?.primary || '#FA4616'
      : props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.15)'
      : '#CBD5E1'};
  transition: background 0.2s ease;
  position: relative;
`;

const RadioThumb = styled.div`
  position: absolute;
  top: 2px;
  left: ${(props) => (props.checked ? '22px' : '2px')};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: left 0.2s ease;
`;

/**
 * SettingsGroup - A grouped settings component with toggle options
 *
 * @param {string} title - Title for the settings group
 * @param {string} description - Description text below the title
 * @param {Array} options - Array of options {label, value, disabled?}
 * @param {string|boolean} value - Currently selected value
 * @param {Function} onChange - Called when selection changes
 * @param {string} name - Name attribute for the group
 * @param {boolean} showAllToggle - Whether to show "All" toggle in header
 * @param {string} allValue - Value when "All" is selected
 */
const SettingsGroup = ({
  title,
  description,
  options = [],
  value,
  onChange,
  name,
  showAllToggle = false,
  allValue = 'all',
  ...props
}) => {
  const handleOptionClick = (optionValue, disabled) => {
    if (disabled) return;
    if (onChange) {
      onChange({
        target: {
          name,
          value: optionValue,
        },
      });
    }
  };

  const isAllSelected = value === allValue;

  return (
    <Container {...props}>
      <Header>
        <HeaderContent>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
        </HeaderContent>
        {showAllToggle && (
          <HeaderRight>
            <AllLabel>All</AllLabel>
            <RadioWrapper onClick={() => handleOptionClick(allValue, false)}>
              <HiddenInput
                type="radio"
                name={name}
                value={allValue}
                checked={isAllSelected}
                onChange={() => handleOptionClick(allValue, false)}
              />
              <RadioTrack checked={isAllSelected}>
                <RadioThumb checked={isAllSelected} />
              </RadioTrack>
            </RadioWrapper>
          </HeaderRight>
        )}
      </Header>
      <OptionsList>
        {options.map((option) => {
          const isChecked = value === option.value;
          const isDisabled = option.disabled;

          return (
            <OptionItem
              key={option.value}
              disabled={isDisabled}
              onClick={() => handleOptionClick(option.value, isDisabled)}
            >
              <OptionLabel>{option.label}</OptionLabel>
              <RadioWrapper>
                <HiddenInput
                  type="radio"
                  name={name}
                  value={option.value}
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={() => handleOptionClick(option.value, isDisabled)}
                />
                <RadioTrack checked={isChecked}>
                  <RadioThumb checked={isChecked} />
                </RadioTrack>
              </RadioWrapper>
            </OptionItem>
          );
        })}
      </OptionsList>
    </Container>
  );
};

SettingsGroup.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onChange: PropTypes.func,
  name: PropTypes.string.isRequired,
  showAllToggle: PropTypes.bool,
  allValue: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

export default SettingsGroup;
